'use client'
import { useContext, useCallback } from 'react';
import { DynamicContext } from '../DynamicContext.js';
import { useLoadingAndLifecycle } from '../../../store/state/loadingAndLifecycle/loadingAndLifecycle.js';
import { useOverrides } from './useOverrides/useOverrides.js';

// This is external context hook which is exposed to customers
const useDynamicContext = () => {
    const context = useContext(DynamicContext);
    if (context === undefined) {
        throw new Error('useDynamicContext must be used within a DynamicContextProvider');
    }
    const { accountSwitchState, authMode, awaitingSignatureState, getNameService, handleLogOut, removeWallet, loadingNetwork, locale, loginWithEmail, multiWalletWidgetState, network, networkConfigurations, qrcodeUri, refetchProjectSettings, sdkHasLoaded, setLogInWithEmail, setMultiWalletWidgetState, setShowAuthFlow, setShowDynamicUserProfile, setShowQrcodeModal, shadowDOMEnabled, showAuthFlow, showQrcodeModal, user, userWithMissingInfo, bridgeChains, bridgeChainsToConnect, primaryWallet, setShowBridgeWidget, sendWagmiSettings, showDynamicUserProfile, selectedTabIndex, setSelectedTabIndex, } = context;
    const { setAuthMode } = useOverrides(context);
    const externalSetShowAuthFlow = useCallback((value) => {
        setShowAuthFlow(value, {
            clearErrors: true,
            emitCancelAuth: true,
            ignoreIfIsEmbeddedWidget: true,
            initializeWalletConnect: true,
            performMultiWalletChecks: true,
        });
    }, [setShowAuthFlow]);
    const { initialWalletVerificationInProgress } = useLoadingAndLifecycle();
    return {
        accountSwitchState,
        authMode,
        awaitingSignatureState,
        bridgeChains,
        bridgeChainsToConnect,
        getNameService,
        handleLogOut,
        handleUnlinkWallet: removeWallet,
        loadingNetwork,
        locale,
        loginWithEmail,
        multiWalletWidgetState,
        network,
        networkConfigurations,
        primaryWallet: initialWalletVerificationInProgress ? null : primaryWallet,
        qrcodeUri,
        refetchProjectSettings,
        removeWallet,
        sdkHasLoaded,
        selectedTabIndex,
        sendWagmiSettings,
        setAuthMode,
        setLogInWithEmail,
        setMultiWalletWidgetState,
        setSelectedTabIndex,
        setShowAuthFlow: externalSetShowAuthFlow,
        setShowBridgeWidget,
        setShowDynamicUserProfile,
        setShowQrcodeModal,
        shadowDOMEnabled,
        showAuthFlow,
        showDynamicUserProfile,
        showQrcodeModal,
        user,
        userWithMissingInfo,
    };
};

export { useDynamicContext };
